/**
 * R3-C label snap — replace extracted label positions with CAD truth.
 *
 * The Hatzav forensics (2026-08-28, visual ground-proofing against the
 * original sheet) proved: where DXF and extractor disagree, the DXF text
 * center sits ON the printed label and the extractor center does not (the
 * merge-family defect drags bboxes sideways, sometimes uniformly across a
 * whole label family). So where a DXF label matches a layout label, the DXF
 * position — mapped through the diversity-consensus fit — becomes the
 * label's position. Reversible: the original extractor position is kept in
 * `entity.dxfSnap`; a re-snap preserves the FIRST original.
 *
 * Pure computation here; the store write lives with the caller-facing
 * `snapLabelsToDxf` / `unsnapLabels`.
 */

import { useProjectStore } from '../stores/projectStore';
import { fetchIngestJson } from '../hooks/useDxfIngestPoller';
import {
  matchPairs, fitPairsRobust, shiftPairs, applyDxfMatrix, normalizeLabel,
  layoutEntitySig, REG_ALGO,
} from './dxfRegistration';
import type { DxfTextEntry } from './dxfRegistration';
import type { LayoutTextEntity, SiteLayout } from '../domain/layout/types';
import type { Overlay } from '../stores/projectStore';

export interface SnapStats {
  matched: number;
  snapped: number;
  medianMovePt: number;
  movedOver3pt: number;
  rmsPt: number;
}

/** Pure: snapped entity array + move statistics. Exported for tests. */
export function computeSnappedEntities(
  entities: LayoutTextEntity[],
  texts: DxfTextEntry[],
): {
  entities: LayoutTextEntity[];
  stats: SnapStats;
  matrix: [number, number, number, number, number, number];
  bias?: [number, number];
} | null {
  const pairs = matchPairs(texts, entities);
  const fit = fitPairsRobust(pairs);
  if (!fit) return null;

  // DXF position per matched normalized label (unique on both sides by
  // construction of matchPairs), with the fit's solved anchor bias applied —
  // the matrix was fitted against corrected anchors, so raw ones would land
  // the snapped labels off by exactly the bias.
  const dxfByKey = new Map<string, [number, number]>();
  for (const p of fit.bias ? shiftPairs(pairs, fit.bias[0], fit.bias[1]) : pairs) {
    dxfByKey.set(p.key, p.src);
  }

  const moves: number[] = [];
  let snapped = 0;
  const next = entities.map((e) => {
    const src = dxfByKey.get(normalizeLabel(e.normalizedText));
    if (!src) return e;
    const [cx, cy] = applyDxfMatrix(fit.matrix, src[0], src[1]);
    const move = Math.hypot(cx - e.cx, cy - e.cy);
    moves.push(move);
    snapped++;
    return {
      ...e,
      cx,
      cy,
      // Keep the FIRST original — a re-snap must not overwrite provenance
      // with an already-snapped position.
      dxfSnap: e.dxfSnap ?? { cx: e.cx, cy: e.cy },
      // cx/cy are CAD truth again — the label rejoins the DXF anchor set
      manuallyMoved: undefined,
    };
  });
  moves.sort((a, b) => a - b);
  const median = moves.length ? moves[Math.floor(moves.length / 2)] : 0;
  return {
    entities: next,
    matrix: fit.matrix,
    bias: fit.bias,
    stats: {
      matched: pairs.length,
      snapped,
      medianMovePt: Math.round(median * 100) / 100,
      movedOver3pt: moves.filter((m) => m > 3).length,
      rmsPt: fit.rmsPt,
    },
  };
}

/** Pure: restore original extractor positions. Exported for tests. */
export function computeUnsnappedEntities(entities: LayoutTextEntity[]): { entities: LayoutTextEntity[]; restored: number } {
  let restored = 0;
  const next = entities.map((e) => {
    if (!e.dxfSnap) return e;
    restored++;
    const { dxfSnap, ...rest } = e;
    return { ...rest, cx: dxfSnap.cx, cy: dxfSnap.cy };
  });
  return { entities: next, restored };
}

function targetLayout(overlay: Overlay): SiteLayout | null {
  const reg = overlay.dxf?.registration;
  if (overlay.dxf?.status !== 'ready' || reg?.kind !== 'layout') return null;
  const data = useProjectStore.getState().data;
  const layouts = data.siteLayouts ?? (data.siteLayout ? [data.siteLayout] : []);
  return layouts.find((l) => l.id === reg.layoutId) ?? null;
}

function writeEntities(layout: SiteLayout, entities: LayoutTextEntity[]): void {
  const st = useProjectStore.getState();
  // updateSiteLayoutEntities acts on the ACTIVE layout; point it first.
  if (st.activeLayoutId !== layout.id) st.setActiveLayoutId(layout.id);
  useProjectStore.getState().updateSiteLayoutEntities(entities);
}

/** Snap the registration layout's labels to the DXF. Null when there is no
 *  layout-registered ready DXF to snap against. */
export async function snapLabelsToDxf(overlay: Overlay): Promise<SnapStats | null> {
  const layout = targetLayout(overlay);
  const dxf = overlay.dxf;
  if (!layout || !dxf) return null;
  const textsDoc = await fetchIngestJson<{ texts: DxfTextEntry[] }>(dxf.outPrefix, 'texts.json');
  if (!textsDoc?.texts?.length) return null;
  const result = computeSnappedEntities(layout.textEntities, textsDoc.texts);
  if (!result) return null;
  writeEntities(layout, result.entities);
  // The registration now describes truth-anchored labels — restamp it so the
  // picker's alignment line reflects the robust fit.
  useProjectStore.getState().updateOverlay(overlay.id, {
    dxf: {
      ...dxf,
      registration: {
        kind: 'layout', layoutId: layout.id,
        matrix: result.matrix, matched: result.stats.matched, rmsPt: result.stats.rmsPt,
        entitySig: layoutEntitySig(result.entities),
        algo: REG_ALGO,
      },
    },
  });
  useProjectStore.getState().flushPendingEntityWrites().catch(() => {});
  return result.stats;
}

/** Restore all snapped labels of the registration layout. */
export function unsnapLabels(overlay: Overlay): number {
  const layout = targetLayout(overlay);
  if (!layout) return 0;
  const { entities, restored } = computeUnsnappedEntities(layout.textEntities);
  if (restored > 0) writeEntities(layout, entities);
  return restored;
}

/** Whether the registration layout carries any snapped labels. */
export function hasSnappedLabels(overlay: Overlay): boolean {
  const layout = targetLayout(overlay);
  return !!layout?.textEntities.some((e) => e.dxfSnap);
}
